/**
 * Go-to-Market Strategy Hub
 * Portfolio-wide competitive intelligence dashboard
 * 
 * Features:
 * - Portfolio Health metrics (BSR improving/declining)
 * - Market Alerts (BSR drops, price changes, new competitors)
 * - Top Performers / Needs Attention lists
 * - Price Position Map
 * - Weekly Action Items (AI-generated recommendations)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { config } from '../../config/environment';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Award,
  BarChart3,
  RefreshCw,
  ExternalLink,
  Bell,
  Loader2,
  Package,
  DollarSign,
  Zap,
  ChevronRight,
  Clock,
  Star
} from 'lucide-react';

const API = config.apiBaseUrl;
const MOCK_TENANT_ID = 'tenant-123';

// Custom Tooltip for Price Position Chart
const PricePositionTooltip = ({ payload }) => {
  if (!payload || !payload[0]) return null;
  const data = payload[0].payload;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded p-2 text-xs">
      <p className="font-medium text-white">{data.name}</p>
      <p className="text-gray-400">Your Price: {formatCurrency(data.your_price)}</p>
      <p className="text-gray-400">Market Avg: {formatCurrency(data.market_avg)}</p>
      <p className={data.diff > 0 ? 'text-red-400' : 'text-green-400'}>
        {data.diff > 0 ? '+' : ''}{data.diff}% vs market
      </p>
    </div>
  );
};

// Format BSR for display
const formatBSR = (value) => {
  if (!value && value !== 0) return 'N/A';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toLocaleString();
};

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

// ============================================================================
// Alerts Section Component
// ============================================================================
const AlertsSection = ({ alerts = [], loading = false }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-900/30 border-red-700 text-red-300';
      case 'medium': return 'bg-amber-900/30 border-amber-700 text-amber-300';
      case 'low': return 'bg-blue-900/30 border-blue-700 text-blue-300';
      default: return 'bg-gray-800 border-gray-700 text-gray-300';
    }
  };
  
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'medium': return <AlertCircle className="w-4 h-4 text-amber-400" />;
      case 'low': return <Bell className="w-4 h-4 text-blue-400" />;
      default: return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (alerts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
        <p className="text-sm">No active alerts - portfolio looks healthy!</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {alerts.slice(0, 5).map((alert, idx) => (
        <div
          key={alert.id || idx}
          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity ${getSeverityColor(alert.severity)}`}
        >
          {getSeverityIcon(alert.severity)}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{alert.message}</p>
            <p className="text-xs opacity-70">
              {alert.product_name} • {new Date(alert.created_at).toLocaleDateString()}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 opacity-50" />
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Portfolio Health Component
// ============================================================================
const PortfolioHealth = ({ stats, loading = false }) => {
  const healthCards = [
    {
      label: 'Total SKUs',
      value: stats?.total_skus || 0,
      icon: <Package className="w-5 h-5" />,
      color: 'text-blue-400',
      bgColor: 'bg-blue-900/30'
    },
    {
      label: 'BSR Improving',
      value: stats?.bsr_improving || 0,
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'text-green-400',
      bgColor: 'bg-green-900/30',
      badge: stats?.bsr_improving > 0 ? '✅' : null
    },
    {
      label: 'BSR Declining',
      value: stats?.bsr_declining || 0,
      icon: <TrendingDown className="w-5 h-5" />,
      color: 'text-red-400',
      bgColor: 'bg-red-900/30',
      badge: stats?.bsr_declining > 5 ? '⚠️' : null
    },
    {
      label: 'No ASIN Set',
      value: stats?.no_asin || 0,
      icon: <AlertCircle className="w-5 h-5" />,
      color: 'text-amber-400',
      bgColor: 'bg-amber-900/30'
    }
  ];
  
  if (loading) {
    return (
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-800 rounded-lg p-4 animate-pulse">
            <div className="h-8 w-8 bg-gray-700 rounded mb-2"></div>
            <div className="h-6 w-16 bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-4 gap-4">
      {healthCards.map((card, idx) => (
        <Card key={idx} className={`${card.bgColor} border-gray-700`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className={card.color}>{card.icon}</div>
              {card.badge && <span className="text-lg">{card.badge}</span>}
            </div>
            <div className="text-2xl font-bold text-white">{card.value}</div>
            <div className="text-xs text-gray-400">{card.label}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============================================================================
// Top Performers / Needs Attention Component
// ============================================================================
const ProductRankings = ({ topPerformers = [], needsAttention = [], loading = false }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-4">
        {[...Array(2)].map((_, i) => (
          <Card key={i} className="bg-gray-800 border-gray-700">
            <CardContent className="py-8 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Top Performers */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Award className="w-4 h-4 text-yellow-400" />
            Top Performers (by BSR)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topPerformers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No BSR data available</p>
          ) : (
            <div className="space-y-2">
              {topPerformers.map((product, idx) => (
                <div key={product.id || idx} className="flex items-center gap-3 p-2 rounded hover:bg-gray-700/50">
                  <span className="text-lg font-bold text-yellow-400 w-6">#{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{product.name}</p>
                    <p className="text-xs text-gray-400">{product.sku}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-green-400">#{formatBSR(product.bsr)}</div>
                    {product.trend > 0 && (
                      <div className="flex items-center gap-1 text-xs text-green-400">
                        <TrendingUp className="w-3 h-3" />
                        +{product.trend}%
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Needs Attention */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            Needs Attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          {needsAttention.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">All products performing well!</p>
          ) : (
            <div className="space-y-2">
              {needsAttention.map((product, idx) => (
                <div key={product.id || idx} className="flex items-center gap-3 p-2 rounded hover:bg-gray-700/50">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{product.name}</p>
                    <p className="text-xs text-gray-400">{product.sku}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-red-400">#{formatBSR(product.bsr)}</div>
                    {product.days_declining > 0 && (
                      <div className="text-xs text-red-400">
                        {product.days_declining}d declining
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// Price Position Map Component
// ============================================================================
const PricePositionMap = ({ data = [], loading = false }) => {
  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }
  
  if (data.length === 0) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-400" />
            Price Position Map
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center">
          <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm text-gray-500">Add competitor data to see price positioning</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-400" />
          Price Position vs Market Average
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 100, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
              <XAxis 
                type="number" 
                tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`}
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                domain={['dataMin - 10', 'dataMax + 10']}
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                width={90}
              />
              <Tooltip content={<PricePositionTooltip />} />
              <ReferenceLine x={0} stroke="#6B7280" />
              <Bar dataKey="diff" radius={[0, 4, 4, 0]}>
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.diff > 0 ? '#EF4444' : '#22C55E'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-6 mt-2 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            Below Market (Good)
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded"></div>
            Above Market
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Weekly Actions Component
// ============================================================================
const WeeklyActions = ({ actions = [], loading = false }) => {
  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Zap className="w-4 h-4 text-purple-400" />
          Recommended Actions
          <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-700 ml-2">
            AI Generated
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {actions.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm text-gray-500">No urgent actions needed this week</p>
          </div>
        ) : (
          <div className="space-y-3">
            {actions.map((action, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-gray-700/50 rounded-lg">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  action.priority === 'high' ? 'bg-red-900 text-red-300' :
                  action.priority === 'medium' ? 'bg-amber-900 text-amber-300' :
                  'bg-blue-900 text-blue-300'
                }`}>
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-white">{action.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[10px]">
                      {action.type}
                    </Badge>
                    {action.impact && (
                      <span className="text-[10px] text-gray-500">
                        Potential impact: {action.impact}
                      </span>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-xs text-blue-400">
                  View
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Main Go-to-Market Strategy Component
// ============================================================================
const GoToMarketStrategy = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [portfolioStats, setPortfolioStats] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [pricePositions, setPricePositions] = useState([]);
  const [weeklyActions, setWeeklyActions] = useState([]);

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch portfolio stats
      const statsResponse = await axios.get(`${API}/market-intelligence/portfolio-stats`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      setPortfolioStats(statsResponse.data);
      
      // Fetch alerts
      const alertsResponse = await axios.get(`${API}/market-intelligence/alerts`, {
        params: { tenant_id: MOCK_TENANT_ID, limit: 5 }
      });
      setAlerts(alertsResponse.data.alerts || []);
      
      // Fetch rankings
      const rankingsResponse = await axios.get(`${API}/market-intelligence/rankings`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      setTopPerformers(rankingsResponse.data.top_performers || []);
      setNeedsAttention(rankingsResponse.data.needs_attention || []);
      
      // Fetch price positions
      const pricesResponse = await axios.get(`${API}/market-intelligence/price-positions`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      setPricePositions(pricesResponse.data.positions || []);
      
      // Fetch weekly actions
      const actionsResponse = await axios.get(`${API}/market-intelligence/weekly-actions`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      setWeeklyActions(actionsResponse.data.actions || []);
      
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      // Don't show error toast on initial load - data might not exist yet
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  // Initial load
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Target className="w-6 h-6 text-purple-400" />
            Go-to-Market Strategy
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Portfolio-wide competitive intelligence
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs text-gray-400">
            <Clock className="w-3 h-3 mr-1" />
            Last updated: {new Date().toLocaleTimeString()}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-xs"
          >
            {refreshing ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <RefreshCw className="w-3 h-3 mr-1" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Alerts Section */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bell className="w-4 h-4 text-red-400" />
            Active Alerts
            {alerts.length > 0 && (
              <Badge className="bg-red-600 text-white text-[10px] ml-2">
                {alerts.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AlertsSection alerts={alerts} loading={loading} />
        </CardContent>
      </Card>

      {/* Portfolio Health */}
      <div>
        <h2 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
          <Star className="w-4 h-4" />
          Portfolio Health
        </h2>
        <PortfolioHealth stats={portfolioStats} loading={loading} />
      </div>

      {/* Rankings */}
      <ProductRankings 
        topPerformers={topPerformers} 
        needsAttention={needsAttention}
        loading={loading}
      />

      {/* Price Position Map */}
      <PricePositionMap data={pricePositions} loading={loading} />

      {/* Weekly Actions */}
      <WeeklyActions actions={weeklyActions} loading={loading} />
    </div>
  );
};

export default GoToMarketStrategy;
